import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DownloadSimple, DeviceMobile } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * "Instalar app" button. Triggers the PWA install prompt when available
 * (Chrome / Edge / Android). On iOS Safari it shows manual instructions.
 */
export default function InstallAppButton() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [iosHelpOpen, setIosHelpOpen] = useState(false);

  useEffect(() => {
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setInstalled(isStandalone);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const isIos =
    /iphone|ipad|ipod/i.test(window.navigator.userAgent) &&
    !window.navigator.userAgent.includes("CriOS");

  const handleClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferredPrompt(null);
    } else if (isIos) {
      setIosHelpOpen(true);
    } else {
      setIosHelpOpen(true); // also show fallback help for other browsers
    }
  };

  return (
    <>
      <button
        onClick={handleClick}
        data-testid="install-app-btn"
        className="w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-semibold transition-colors bg-zinc-900 text-white border border-[var(--brand,#E31E24)] hover:bg-[var(--brand,#E31E24)]"
      >
        <DownloadSimple size={18} weight="bold" />
        {t('common.install')}
      </button>

      <Dialog open={iosHelpOpen} onOpenChange={setIosHelpOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DeviceMobile size={22} weight="duotone" className="text-[var(--brand,#E31E24)]" />
              Instalar HSE como app
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-700">
            {isIos ? (
              <>
                <p>En iPhone / iPad (Safari):</p>
                <ol className="list-decimal pl-5 space-y-1.5">
                  <li>Toca el botón <strong>Compartir</strong> (cuadro con flecha hacia arriba) en la barra inferior.</li>
                  <li>Desliza y selecciona <strong>Agregar a pantalla de inicio</strong>.</li>
                  <li>Confirma con <strong>Agregar</strong>. El ícono HSE aparecerá en tu pantalla principal.</li>
                </ol>
              </>
            ) : (
              <>
                <p>Si tu navegador no muestra el botón automático, puedes instalar manualmente:</p>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li><strong>Chrome / Edge (Android, Windows, Mac):</strong> menú ⋮ → "Instalar HSE" o "Agregar a pantalla de inicio".</li>
                  <li><strong>Safari (Mac):</strong> menú Archivo → "Agregar al Dock".</li>
                  <li><strong>Firefox Android:</strong> menú ⋮ → "Instalar".</li>
                </ul>
                <p className="text-xs text-slate-500 pt-2">
                  Una vez instalada, podrás abrir HSE desde el icono nativo sin abrir el navegador.
                </p>
              </>
            )}
          </div>
          <DialogFooter>
            <Button className="bg-[var(--brand,#E31E24)] hover:bg-[#b81920]" onClick={() => setIosHelpOpen(false)}>
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
